import { useEffect, useState } from 'react';

/**
 * Tailwind's `sm` breakpoint. Below this width we treat the screen as mobile.
 *
 * Using a media query rather than `window.innerWidth` directly so the value
 * updates when the viewport changes (e.g. device rotation) without each
 * consumer wiring its own resize listener.
 */
const MOBILE_QUERY = '(max-width: 639px)';

/**
 * True when the viewport is at most the Tailwind `sm` width.
 *
 * The initial state is read synchronously from `matchMedia` so the first
 * render already knows the layout — there is no flash of desktop layout on a
 * mobile device while the effect runs. SSR is not a concern: the hook only
 * runs in the browser, and `matchMedia` is gated on a typeof check.
 */
export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(MOBILE_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(MOBILE_QUERY);
    const onChange = (event: MediaQueryListEvent) => setIsMobile(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return isMobile;
}
